<template>
	<v-form ref="form" @submit.prevent="submit" :disabled="loading || submitLoading">
		<!-- attribute dialog -->
		<v-dialog
			v-model="attributeDialog"
			persistent
			max-width="700"
		>
			<v-card class="rounded-medium">
				<v-form ref="attributeForm" @submit.prevent="updateAttributes">
					<v-card-title
						class="justify-center text-uppercase text-h6"
						:class="{ 'mb-3': !multiLangInputs, 'pb-2': multiLangInputs }"
					>
						{{ attributeId ? $t('edit') : $t('add')}} {{$t('Categories.attribute')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- tabs -->
						<v-lang-tabs
							v-model="attributeLang"
							:checked-langs="acceptedToSaveAttributesLangs"
							background-color="bgColor"
							class="mb-4"
						/>

						<!-- inputs -->
						<v-row
							align="center"
							dense
							class="ma-0"
						>
							<!-- attribute image -->
							<v-col v-if="useUploadAttributeImage" cols="auto">
								<bee-drop-img
									v-model="attributeData.image"
									dashed
									border-width="2"
									:border-color="isSubmitted & !attributeData.image ? '#f00' : '#ccc'"
									radius="11"
									:height="60"
									:width="60"
									contain
									blurable
									clearable
									hide-details
									hide-image-name
									hide-drag-drop-text
									disable-file-picker
									@remove-image="fileManagerFiles.mainImage = {}"
									@click="$eventBus.$emit('file-manager', { images: true }, (url, file, uploaded) => {
										attributeData.image = url;
										attributeData.mainImage = file;
									}, null, (uploadedFile) => {
										attributeData.image = uploadedFile.url;
										attributeData.mainImage = uploadedFile;
									})"
								></bee-drop-img>
							</v-col>

							<!-- attribute name -->
							<v-col :cols="4">
								<v-text-field
									v-model="attributeData.name[attributeLang]"
									:label="$t('inputs.attribute-name')"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- attribute type -->
							<v-col
								v-if="useAttributeTypes"
								sm="3"
								cols="12"
							>
								<v-autocomplete
									v-model="attributeData.type"
									:label="$t('inputs.attribute-type')"
									:items="attributeTypes"
									item-text="name"
									item-value="id"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.requiredAsNumber"
									@change="
										![
											attributeType.multiChoice,
											attributeType.multiOptions,
											attributeType.yesNoMultiChoice,
										].includes(attributeData.type)
											? attributeData.options = createObjectFromLangs({}, [])
											:  null
									"
								></v-autocomplete>
							</v-col>

							<!-- attribute filterType options -->
							<v-col
								v-if="useAttributeFilterTypes"
								sm="3"
								cols="12"
							>
								<v-autocomplete
									v-model="attributeData.filterType"
									:label="$t('inputs.attribute-filter-type')"
									:items="attributeFilterTypes"
									item-text="name"
									item-value="id"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.requiredAsNumber"
								></v-autocomplete>
							</v-col>

							<!-- is featured -->
							<v-col v-if="useFeatured.attributes" cols="auto">
								<v-switch
									v-model="attributeData.isFeatured"
									:label="$t('inputs.is-featured')"
									:disabled="attributes.filter(c => c.isFeatured).length >= 5 && !attributeData.isFeatured"
									hide-details
									class="mt-0 ms-2 mb-1 pt-0"
								></v-switch>
							</v-col>

							<!-- attributes options -->
							<v-col
								v-if="useAttributeTypes && [
									attributeType.multiChoice,
									attributeType.multiOptions,
									attributeType.yesNoMultiChoice,
								].includes(attributeData.type)"
								cols="12"
							>
								<span class="text-body-2">
									{{
										$t('Categories.$-options', {
											name: attributeData.name[attributeLang] || $t('Categories.attribute')
										})
									}}
								</span>
								<v-card max-height="300" class="rounded-medium my-1 pa-2">
									<v-card-text
										class="overflow-y-auto pa-0"
										:style="{ maxHeight: '250px' }"
									>
										<bee-attribute-input
											v-model="attributeData.options[attributeLang]"
											:type="attributeType.multiChoice"
											outlined
											dense
											hide-details
											:rules="
												attributeData.options[attributeLang].length === 0 ? rules.required : []
											"
											class="d-flex flex-wrap"
											:multiple-inputs-bind="(value, i) => ({
												label: $t('Categories.option') + ' ' + (i + 1),
												style: { width: '32%', maxWidth: '32%', margin: '3px' }
											})"
										/>
									</v-card-text>
								</v-card>
								<p class="text-end text-body-2 mb-0">
									{{$t('Categories.options-count')}}: 
									<span class="font-weight-medium">
										{{attributeData.options[attributeLang].length}}
									</span>
								</p>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submit"
							color="primary"
							class="rounded-small"
						>
							{{attributeId ? $t('edit') : $t('add')}}
						</v-btn>
						<v-btn
							@click="attributeDialog = false"
							class="rounded-small"
						>
							{{$t('cancel')}}
						</v-btn>
					</v-card-actions>
				</v-form>
			</v-card>
		</v-dialog>

		<!-- tabs -->
		<v-lang-tabs
			v-if="multiLangInputs"
			v-model="lang"
			:checked-langs="acceptedToSaveLangs"
			:disable-non-selected="submitLoading"
			background-color="white"
			class="mb-4"
			@change="updateFieldsFromDraft"
		/>

		<v-row class="mt-0 mb-5">
			<v-col lg="9" md="8" cols="12" order-md="0" order="1" class="pb-md-0">
				<!-- inputs -->
				<v-row dense class="mt-0 mb-2" :class="{ 'flex-column': !useAttributeTypes }">
					<!-- name -->
					<v-col lg="3" md="4" sm="6" cols="12">
						<v-text-field
							v-model="name"
							:label="$t('inputs.name')"
							outlined
							dense
							:loading="loading"
							hide-details
							class="rounded-small"
							:rules="rules.required"
						></v-text-field>
					</v-col>

					<!-- set -->
					<v-col
						v-if="showSets && !useCategoryTree"
						lg="3"
						md="4"
						sm="6"
						cols="12"
					>
						<v-autocomplete
							v-model="setIds"
							:items="sets"
							item-text="name"
							item-value="id"
							:label="$t('inputs.set')"
							outlined
							dense
							:loading="loading || submitLoading"
							multiple
							hide-details
							class="rounded-small"
							:rules="rules.requiredAsArray"
						></v-autocomplete>
					</v-col>
					
					<!-- summary -->
					<v-col v-if="useDescriptionFor.categories" lg="3" md="4" sm="6" cols="12">
						<v-text-field
							v-model="summary"
							:label="$t('inputs.summary')"
							outlined
							dense
							multiple
							hide-details
							:loading="loading || submitLoading"
							class="rounded-small"
						></v-text-field>
					</v-col>
					
					<!-- parent categories -->
					<v-col v-if="useCategoryTree" lg="3" md="4" sm="6" cols="12">
						<v-autocomplete
							v-model="parentCategoryId"
							:items="categories.filter(c => !c.deletedAt)"
							item-text="name"
							item-value="id"
							:label="$t('inputs.parent-category')"
							:loading="assetsLoading"
							outlined
							dense
							hide-details
							class="rounded-small"
							@change="attributes = []"
						></v-autocomplete>
					</v-col>
					
					<!-- is-featured -->
					<v-col v-if="useFeatured.categories" cols="12">
						<v-switch
							v-model="isFeatured"
							:label="$t('inputs.is-featured')"
							hide-details
							class="mt-0 ms-2 mb-1 pt-0"
						></v-switch>
					</v-col>

					<!-- attributes -->
					<v-col v-if="useCategoryAttributes" cols="12">
						<div class="d-flex align-center mb-1">
							<span class="text-body-1 font-weight-bold">
								<template v-if="parentCategoryId">
									Subcategory
								</template>
								{{$t('attributes')}}
							</span>
							<v-btn
								icon
								:disabled="loading || submitLoading"
								@click="attributeDialog = true"
							>
								<v-icon>mdi-plus-circle-outline</v-icon>
							</v-btn>
						</div>

						<!-- attributes table -->
						<v-data-table
							:headers="attributeTableHeaders"
							:items="loading ? [] : attributes"
							:loading="loading"
							:item-class="item => ({
								'drag-item': dragIndex === attributes.indexOf(item),
							})"
							:fixed-header="attributes.length > 8"
							:height="attributes.length > 8 ? $vuetify.breakpoint.height - 300 : null"
							:items-per-page="-1"
							hide-default-footer
							disable-sort
							no-data-text="No attributes entered"
							dense
							class="rounded-medium elevation-2"
						>
							<!-- image -->
							<template v-slot:item.image="{ item }">
								<bee-drop-img
									:value="item.image || require('@/assets/placeholder-image.png')"
									border-width="0"
									radius="11"
									:height="50"
									:width="50"
									contain
									hide-details
									hide-image-name
									hide-drag-drop-text
									disabled
									style="z-index: 0;"
								></bee-drop-img>
							</template>
							
							<!-- name -->
							<template v-slot:item.name="{ item }">
								{{item.name[lang]}}
							</template>
							
							<!-- type -->
							<template v-slot:item.type="{ item }">
								{{attributeTypes.find(c => c.id === item.type)?.name}}
							</template>
							
							<!-- filterType -->
							<template v-slot:item.filterType="{ item }">
								{{attributeFilterTypes.find(c => c.id === item.filterType)?.name}}
							</template>
							
							<!-- isFeatured -->
							<template v-slot:item.isFeatured="{ item }">
								<v-icon v-if="item.isFeatured">
									mdi-check-circle-outline
								</v-icon>
								<span v-else>-</span>
							</template>
							
							<!-- options -->
							<template v-slot:item.options="{ item }">
								<template v-if="!item.options[lang].length">-</template>
								
								<v-menu v-else open-on-click max-width="400">
									<template v-slot:activator="{ attrs, on }">
										<div v-bind="attrs" v-on="on">
											<v-chip-group column>
												<v-chip
													v-for="(option, index) in item.options[lang].slice(0, 10)"
													:key="index"
													x-small
													class="mt-0 mb-1 me-1"
												>
													{{option}}
												</v-chip>
												<v-chip
													v-if="item.options[lang].slice(10).length"
													outlined
													x-small
													color="primary"
													class="mt-0 mb-1 me-1"
												>
													+{{item.options[lang].slice(10).length}}
												</v-chip>
											</v-chip-group>
										</div>
									</template>

									<v-card>
										<v-card-text class="px-2 py-1">
											<p class="mb-0 text-body-2 grey--text text--darken-3">
												{{item.name[lang]}} options
											</p>
											<v-chip-group column class="mb-n1">
												<v-chip
													v-for="(option, index) in item.options[lang]"
													:key="index"
													x-small
													class="mt-0 mb-1 me-1"
												>
													{{option}}
												</v-chip>
											</v-chip-group>
										</v-card-text>
									</v-card>
								</v-menu>
							</template>
							
							<!-- actions -->
							<template v-slot:item.actions="{ item, index }">
								<div
									class="d-flex align-center"
									@dragover.prevent
									@dragend="dragIndex = null;"
									@dragenter="dragIndex = index"
									@drop="
										orderItemId !== item.id
										? (() => {
											getDataOnDrop($event, index, attributes);
											if ($route.params.id) {
												isOrderChanged = true;
												attributes.forEach((c, i, arr) => arr[i].id = i)
											}
										})()
										: null;
									"
								>
									<v-btn
										color="blue darken-4"
										:width="30"
										:height="30"
										dark
										small
										min-width="20px"
										class="pa-3"
										@click="
											attributeId = item.id;
											attributeDialog = true;
										"
									><v-icon>mdi-pencil</v-icon></v-btn>

									<v-btn
										icon
										:disabled="loading || submitLoading"
										@click="() => {
											const removeId = item.id;
											attributes.splice(index, 1);
											attributes.forEach((c, i, arr) => c.id > removeId ? arr[i].id-- : null)
										}"
									>
										<v-icon>mdi-minus-circle-outline</v-icon>
									</v-btn>

									<div
										:draggable="!loading"
										@dragstart="
											orderItemId = item.id;
											setDataOnDrag($event, index);
										"
										:class="{'drag-dots': !loading && !submitLoading}"
									>
										<v-icon :disabled="loading || submitLoading">mdi-drag-vertical</v-icon>
									</div>
								</div>

							</template>
						</v-data-table>

						<v-row
							v-for="(attribute, index) in [] || attributes"
							:key="index"
							align="center"
							dense
							class="ma-0"
							:class="{ 'drag-item': dragIndex === index }"
							@dragover.prevent
							@dragend="dragIndex = null;"
							@dragenter="dragIndex = index"
							@drop="
								orderItemId !== attribute.id
								? (() => {
									getDataOnDrop($event, index, attributes);
									if ($route.params.id) {
										isOrderChanged = true;
										attributes.forEach((c, i, arr) => arr[i].id = i)
									}
								})()
								: null;
							"
						>
							<!-- drag & remove -->
							<v-col cols="auto" class="d-flex align-center">
								<div
									:draggable="!loading"
									@dragstart="
										orderItemId = attribute.id;
										setDataOnDrag($event, index);
									"
									:class="{'drag-dots': !loading && !submitLoading}"
								>
									<v-icon :disabled="loading || submitLoading">mdi-drag-vertical</v-icon>
								</div>
								<v-btn
									icon
									:disabled="loading || submitLoading"
									@click="() => {
										const removeId = attribute.id;
										attributes.splice(index, 1);
										attributes.forEach((c, i, arr) => c.id > removeId ? arr[i].id-- : null)
									}"
								>
									<v-icon>mdi-minus-circle-outline</v-icon>
								</v-btn>
							</v-col>

							<!-- attribute image -->
							<v-col v-if="useUploadAttributeImage" cols="auto">
								<!-- TODO put next line for validation -->
								<!-- :border-color="isSubmitted & !attribute.image ? '#f00' : '#ccc'" -->
								<bee-drop-img
									v-model="attribute.image"
									dashed
									border-width="2"
									radius="11"
									:height="60"
									:width="60"
									contain
									blurable
									clearable
									hide-details
									hide-image-name
									hide-drag-drop-text
									:disabled="submitLoading"
									disable-file-picker
									@remove-image="fileManagerFiles.mainImage = {}"
									@click="$eventBus.$emit('file-manager', { images: true }, (url, file) => {
										attribute.image = url;
										attribute.mainImage = file;
									}, null, true)"
								></bee-drop-img>
							</v-col>

							<!-- attribute name -->
							<v-col
								:cols="
									useUploadAttributeImage
										? (useAttributeTypes && useAttributeFilterTypes ? 3 : 8)
										: 11
								"
							>
								<v-text-field
									v-model="attribute.name[lang]"
									:label="$t('inputs.attribute-name')"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- attribute type -->
							<v-col
								v-if="useAttributeTypes"
								sm="2"
								cols="12"
							>
								<v-autocomplete
									v-model="attribute.type"
									:label="$t('inputs.attribute-type')"
									:items="attributeTypes"
									item-text="name"
									item-value="id"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.requiredAsNumber"
								></v-autocomplete>
							</v-col>

							<!-- attribute filterType options -->
							<v-col
								v-if="useAttributeFilterTypes"
								sm="2"
								cols="12"
							>
								<v-autocomplete
									v-model="attribute.filterType"
									:label="$t('inputs.attribute-filter-type')"
									:items="attributeFilterTypes"
									item-text="name"
									item-value="id"
									outlined
									dense
									hide-details
									class="rounded-medium"
									:rules="rules.requiredAsNumber"
								></v-autocomplete>
							</v-col>

							<!-- is featured -->
							<v-col v-if="useFeatured.attributes" cols="auto">
								<v-switch
									v-model="attribute.isFeatured"
									:label="$t('inputs.is-featured')"
									:disabled="attributes.filter(c => c.isFeatured).length >= 5 && !attribute.isFeatured"
									hide-details
									class="mt-0 ms-2 mb-1 pt-0"
								></v-switch>
							</v-col>

							<!-- attributes options -->
							<v-col
								v-if="useAttributeTypes && [
									attributeType.multiChoice,
									attributeType.multiOptions,
									attributeType.yesNoMultiChoice,
								].includes(attribute.type)"
								cols="12"
							>
								<span class="text-body-2">
									{{
										$t('Categories.$-options', {
											name: attribute.name[lang] || $t('Categories.attribute')
										})
									}}
								</span>
								<bee-attribute-input
									v-model="attribute.options[lang]"
									:type="attributeType.multiChoice"
									outlined
									dense
									hide-details
									:rules="
										attribute.options[lang].length === 0 ? rules.required : []
									"
									class="d-flex flex-wrap"
									:multiple-inputs-bind="(value, i) => ({
										label: $t('Categories.option') + ' ' + (i + 1),
										style: { width: '32%', maxWidth: '32%', margin: '3px' }
									})"
								/>
							</v-col>

							<v-col v-if="index !== attributes.length - 1" cols="12" class="py-2">
								<v-divider/>
							</v-col>
						</v-row>
					</v-col>
				</v-row>

				<!-- description -->
				<template v-if="useDescriptionFor.categories">
					<div class="text-body-1 font-weight-bold text-capitalize mb-3">{{$t('inputs.description')}}</div>
					<text-editor
						v-model="description"
						:loading="loading"
						:error="!Boolean(description) && isSubmitted"
						:use-template-builder="allowTemplateBuilder"
					/>
				</template>
			</v-col>
			
			<v-col v-if="useUploadCategoryImage" cols="auto" class="d-md-block d-none px-1">
				<v-divider vertical/>
			</v-col>

			<!-- drop image -->
			<v-col v-if="useUploadCategoryImage" md cols="12" class="flex-grow-1 pb-md-0">
				<div class="sticky">
					<div class="text-body-1 font-weight-bold mb-3">
						{{$t('upload-images')}}
					</div>
					
					<bee-drop-img
						v-model="image"
						dashed
						border-width="2"
						:border-color="isSubmitted & !image ? '#f00' : '#ccc'"
						radius="11"
						height="250px"
						contain
						blurable
						clearable
						:disabled="submitLoading"
						disable-file-picker
						@remove-image="fileManagerFiles.mainImage = {}"
						@click="$eventBus.$emit('file-manager', { images: true }, (url, file) => {
							image = url;
							fileManagerFiles.mainImage = file;
						}, null, (uploadedFile) => {
							image = uploadedFile.url;
							fileManagerFiles.mainImage = uploadedFile;
						})"
					>
						<template v-slot:drag-text>
							<span class="text-body-1 font-weight-medium">
								{{$t('drag-text')}}
							</span>
							<span class="text-caption mb-0">
								{{$t('supported-text')}}
							</span>
						</template>
					</bee-drop-img>
				</div>
			</v-col>
		</v-row>

		<div class="d-flex">
			<v-btn
				type="submit"
				color="primary"
				class="rounded-small me-2"
				:loading="loading || submitLoading"
				:disabled="loading || submitLoading"
			>
				{{$t('save')}}
			</v-btn>
			<v-btn
				class="rounded-small"
				:disabled="loading || submitLoading"
				:to="{ name: 'categories' }"
			>
				{{$t('back')}}
			</v-btn>
		</div>
	</v-form>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapGetters, mapState } from 'vuex';
import { useCategoryTree, useCategoryAttributes, useFeatured, useUploadCategoryImage, useUploadAttributeImage, useAttributeTypes, useAttributeFilterTypes, allowTemplateBuilder, useDescriptionFor } from '@/configs/global.config'
import { sets } from '@/configs/routes.config';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import { createObjectFromLangs, getDataOnDrop, setDataOnDrag } from '@/helpers/functions';
import langsDraft from '@/mixins/langs-draft';
import { attributeTypes, attributeType, attributeFilterTypes, attributeFilterType } from '@/helpers/enums';

export default {
	name: 'Category',
	mixins: [langsDraft],

	data() {
		return {
			isSubmitted: false,

			attributeTypes,
			attributeType,

			attributeFilterTypes,
			attributeFilterType, 

			// configs
			showSets: sets.show,
			useDescriptionFor,
			useCategoryTree,
			useCategoryAttributes,
			useUploadAttributeImage,
			useAttributeTypes,
			useAttributeFilterTypes,
			useUploadCategoryImage,
			multiLangInputs,
			useFeatured,
			allowTemplateBuilder,

			// file manager
			fileManagerFiles: {
				mainImage: {}
			},

			// filter
			search: null,
			isFiltered: false,

			// helpers
			loading: false,
			assetsLoading: false,
			submitLoading: false,
			lang: defaultLang,
			rules,
			
			dragIndex: null,
			orderItemId: null,
			isOrderChanged: false,

			// draft for languages 
			draft: {},
			draftNames: ['name', 'summary', { name: 'description', value: '' }],

			// attribute
			attributeId: null,
			attributeDialog: false,
			attributeLang: defaultLang,
			attributeData: {
				name: createObjectFromLangs(),
				type: attributeType.string,
				options: createObjectFromLangs({}, []),
				filterType: attributeFilterType.string,
				isFeatured: false,
				image: null,
				mainImage: null
			},

			// data
			name: null,
			setIds: [],
			summary: null,
			description: null,
			parentCategoryId: null,
			attributes: [],
			image: null,
			isFeatured: null,
		}
	},

	watch: {
		attributeDialog(val) {
			if (val) {
				if (this.attributeId) {
					const item = this.attributes.find(c => c.id === this.attributeId);
					
					this.attributeData.name = item.name;
					this.attributeData.type = item.type;
					this.attributeData.options = item.options;
					this.attributeData.filterType = item.filterType;
					this.attributeData.isFeatured = item.isFeatured;
					this.attributeData.image = item.image;
					this.attributeData.mainImage = item.mainImage;
				}
			} else {
				this.attributeId = null;
				this.$refs.attributeForm.resetValidation();
				this.resetAttributesData();
			}
		}
	},

	computed: {
		...mapState({
			categories: state => state.categories.categories,
			sets: state => state.sets.sets.filter(c => !c.deletedAt),
		}),

		...mapGetters({
			getCategoryById: 'categories/getCategoryById',
		}),

		acceptedToSaveLangs() {
			const acceptedLangs = langs.reduce((res, a) => { res[a.key] = false; return res }, {})
			langs.forEach(lang => {
				if (
					this.draft.name[lang.key] &&
					(!useDescriptionFor.categories || this.draft.summary[lang.key]) &&
					(!useDescriptionFor.categories || this.draft.description[lang.key]) &&
					(!useCategoryAttributes || !this.attributes.some(c => !c.name[lang.key]))
				) {
					acceptedLangs[lang.key] = true;
				}
			});
			return acceptedLangs;
		},

		acceptedToSaveAttributesLangs() {
			const acceptedLangs = langs.reduce((res, a) => { res[a.key] = false; return res }, {})
			langs.forEach(lang => {
				if (this.attributeData.name[lang.key]) {
					acceptedLangs[lang.key] = true;
				}
			});
			return acceptedLangs;
		},

		attributeTableHeaders() {
			const headers = [
				{
					text: this.$t('headers.image'),
					value: 'image',
					width: 60,
					align: 'center'
				},
				{
					text: 'name',
					value: 'name',
					width: 160,
					align: 'center'
				},
				{
					text: 'type',
					value: 'type',
					width: 100,
					align: 'center'
				},
				{
					text: 'filter type',
					value: 'filterType',
					width: 100,
					align: 'center'
				},
				{
					text: this.$t('inputs.is-featured'),
					value: 'isFeatured',
					width: 100,
					align: 'center'
				},
				{
					text: 'options',
					value: 'options',
					align: 'center',
				},
				{
					text: 'actions',
					value: 'actions',
					width: 40,
					align: 'center'
				}
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' px-2' : 'px-2';
				headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2';
			}

			return headers
		}
	},

	methods: {
		resetAttributesData() {
			this.attributeData.name = createObjectFromLangs();
			this.attributeData.type = attributeType.string;
			this.attributeData.options = createObjectFromLangs({}, []);
			this.attributeData.filterType = attributeFilterType.string;
			this.attributeData.isFeatured = false;
			this.attributeData.image = null;
			this.attributeData.mainImage = null;
		},

		updateAttributes() {
			if (this.$refs.attributeForm.validate()) {
				if (!this.attributeId) {
					this.attributes.unshift({
						id: this.attributes.length + 1,
						name: this.attributeData.name,
						type: this.attributeData.type,
						options: this.attributeData.options,
						filterType: this.attributeData.filterType,
						isFeatured: this.attributeData.isFeatured,
						image: this.attributeData.image,
						mainImage: this.attributeData.mainImage
					});
					this.resetAttributesData();
					this.$refs.attributeForm.resetValidation();
				} else {
					const index = this.attributes.findIndex(c => c.id === this.attributeId);
					this.attributes[index].name = this.attributeData.name;
					this.attributes[index].type = this.attributeData.type;
					this.attributes[index].options = this.attributeData.options;
					this.attributes[index].filterType = this.attributeData.filterType;
					this.attributes[index].isFeatured = this.attributeData.isFeatured;
					this.attributes[index].image = this.attributeData.image;
					this.attributes[index].mainImage = this.attributeData.mainImage;
					this.attributeDialog = false;
				}
				// this.$eventBus.$emit('show-snackbar', false, 'Entered attributes is updated');
			}
		},

		async submit() {
			this.isSubmitted = true;
			const state = true;
			/* TODO to activate validation remove comments */
			/* let state = this.useUploadCategoryImage ? this.image : true;
			if (useUploadAttributeImage && !this.parentCategoryId) {
				for (let i = 0; i < this.attributes.length; i++) {
					const element = this.attributes[i];
					if (!element.image && !element.imagePath) {
						state = false;
						break;
					}
				}
			} */
			
			if (this.$refs.form.validate() && state) {
				this.submitLoading = true;

				const promises = [];
				let itemData = null;
				try {
					const submitRequest = (
						isDefaultOne,
						{ name, summary, description, lang },
						id = this.$route.params.id
					) => {
						const actionName = id ? 'update' : 'create';
						return this.$store.dispatch(`categories/${actionName}`, {
							id: id,
							name: name,
							setIds: this.setIds,
							categoryId: this.parentCategoryId,
							summary: summary,
							description: description,
							attributes:
								this.attributes.map(c => ({
									name: c.name[lang],
									image: isDefaultOne && typeof c.image !== 'string' ? c.image : null,
									imagePath:
										isDefaultOne && typeof c.image === 'string'
											? c.mainImage.publicPath
											: null,
									imageName:
										isDefaultOne && typeof c.image === 'string'
											? c.mainImage.fileName
											: null,
									type: c.type,
									options: c.options[lang],
									filterType: c.filterType,
									isFeatured: c.isFeatured,
								})),
							image:
								isDefaultOne && typeof this.image !== 'string'
									? this.image
									: null,
							imagePath:
								isDefaultOne && typeof this.image === 'string'
									? this.fileManagerFiles.mainImage.publicPath
									: null,
							imageName:
								isDefaultOne && typeof this.image === 'string'
									? this.fileManagerFiles.mainImage.fileName
									: null,
							isFeatured: this.isFeatured,
							acceptLanguage: lang
						})
					};

					const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
					for (let index = 0; index < filteredLangs.length; index++) {
						const lang = filteredLangs[index].key;

						const name = this.draft.name[lang];
						const summary = this.draft.summary[lang];
						const description = this.draft.description[lang];

						const dataDueLang = { name, summary, description, lang };

						if (name) {
							if (!itemData) {
								await submitRequest(!itemData, dataDueLang).then(data => { itemData = data });

								if (this.isOrderChanged) {
									await this.$store.dispatch('categories/updateAttributesOrder', {
										attributesIdsOrder: this.attributes.map(c => c.attributeId)
									})
								}
							} else {
								promises.push(submitRequest(false, dataDueLang, itemData.id));
							}
						}
					}

					Promise.all(promises)
						.then(() => {
							const message =
							this.$route.params.slug
								? this.$t('$-is-updated-successfully', { name: this.$t('Categories.category') })
								: this.$t('$-is-created-successfully', { name: this.$t('Categories.category') });
							this.$eventBus.$emit('show-snackbar', false, message);

							this.$router.push({ name: 'categories' })
						})
						.catch(() => {
							if (itemData) {
								this.$router.replace({
									name: this.$route.name,
									params: { slug: itemData.slug, id: itemData.id }
								});
							}
						})
						.finally(() => {
							this.submitLoading = false;
						})
				} catch (error) {
					this.submitLoading = false;
					throw error;
				}
			}
		},

		fetchCategory() {
			this.loading = true;
			
			const slug = this.$route.params.slug;

			const promises = [];
			langs.filter(c => multiLangInputs || c.key === defaultLang).forEach((c, index) => {
				promises.push(
					this.$store.dispatch('categories/fetchBySlug', { slug, acceptLanguage: c.key })
						.then(data => {
							if (index === 0) {
								this.setIds = data.setsIds || data.set || [];
								this.parentCategoryId = data.parentId;
								this.image = data.primaryImage.url;
								this.isFeatured = data.isFeatured;
							}
							this.draft.name[c.key] = data.name;
							this.draft.summary[c.key] = data.summary;
							this.draft.description[c.key] = data.description;
							
							// initialling attributes for first language
							if (!this.attributes.length) {
								let parentCategoryAttributesIds = [];

								if (data.parentId) {
									const parentCategory = this.getCategoryById(data.parentId);
									parentCategoryAttributesIds =
										parentCategory ? parentCategory.attributes.map(c => c.id) : [];
								}

								this.attributes =
									data.attributes
										.filter(c => !parentCategoryAttributesIds.includes(c.id))
										.map((attribute, i) => ({
											id: i + 1,
											attributeId: attribute.id,
											name: createObjectFromLangs({ [c.key]: attribute.name }),
											image: attribute.image.url,
											mainImage: {},
											type: attribute.type,
											options: { [c.key]: attribute.options || [] },
											filterType: attribute.filterType,
											isFeatured: Boolean(attribute.isFeatured),
										}))

							// filling texts in attributes for other languages
							} else {
								for (let i = 0; i < data.attributes.length; i++) {
									this.attributes[i].name[c.key] = data.attributes[i].name;
									this.attributes[i].options[c.key] = data.attributes[i].options;
								}
							}
						})
				)
			});
			
			return Promise.all(promises).finally(() => {
				this.name = this.draft.name[this.lang];
				this.summary = this.draft.summary[this.lang];
				this.description = this.draft.description[this.lang];
				this.loading = false;
			});
		},

		// helpers
		createObjectFromLangs,
		setDataOnDrag,
		getDataOnDrop,
	},

	async created() {
		const promises = [
			!this.categories.length ? this.$store.dispatch('categories/fetchAll') : null
		];

		if (this.$route.params.id) {
			this.loading = true;
			await Promise.all(promises);
			if (useCategoryAttributes && multiLangInputs) {
				await this.fetchCategory();
			} else {
				const item = this.getCategoryById(this.$route.params.id);
				this.name = item.name;
				this.setIds = item.setsIds || item.setIds || [];
				this.summary = item.summary;
				this.description = item.description;
				this.parentCategoryId = item.parentId;
				this.image = item.image;
				this.attributes = item.attributes.map((c, i) => ({
					id: i,
					attributeId: c.id,
					name: { [defaultLang]: c.name },
					image: c.image.url,
					mainImage: {},
					type: c.type,
					options: { [defaultLang]: c.options || [] },
					filterType: c.filterType,
					isFeatured: Boolean(c.isFeatured)
				}));
				this.isFeatured = item.isFeatured;
			}
			this.loading = false;
		} else {
			this.assetsLoading = true;
			Promise.all(promises).finally(() => {
				this.assetsLoading = false;
			})
		}
	}
};
</script>

<style>
</style>